import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Button, ButtonToolbar, Form, Modal } from 'react-bootstrap'
import { useWorkroom } from './WorkroomContext'
import { useForm } from 'react-hook-form'
import { updateFolder, updateFile } from 'Frontend/scripts/services/workrooms.service'
import { IWorkroomDocument, IWorkroomFolder, WorkroomItemType } from 'Frontend/scripts/models/workrooms.model'
import { useTranslation } from 'react-i18next'

interface EditFileOrFolderForm {
  name: string
  description: string
}

const WorkroomEditFileButton: FunctionComponent = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { selectedFiles, currentWorkroomId, isLoading, resetSelectedFiles, fetchFilesAndFolders } = useWorkroom()
  const { t } = useTranslation()

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<EditFileOrFolderForm>({})

  const canEditFileOrFolder = useMemo(() => {
    return selectedFiles.length === 1
  }, [selectedFiles])

  const onItemUpdated = () => {
    fetchFilesAndFolders()
    handleClose()
    resetSelectedFiles()
  }

  const onSubmit = (formData: EditFileOrFolderForm) => {
    const editedItem = {
      ...selectedFiles[0],
      name: formData.name,
    }
    if (selectedFiles[0].type === WorkroomItemType.FOLDER) {
      updateFolder(currentWorkroomId as string, editedItem as IWorkroomFolder).then(() => {
        onItemUpdated()
      })
    } else {
      updateFile(currentWorkroomId as string, editedItem as IWorkroomDocument).then(() => {
        onItemUpdated()
      })
    }
  }

  const handleClose = () => {
    reset()
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    if (selectedFiles.length <= 0) return
    setValue('name', selectedFiles[0]?.name)
  }, [selectedFiles])

  return (
    <>
      <Button variant="secondary" className="fw-normal" disabled={!canEditFileOrFolder} onClick={() => setIsOpen(true)}>
        <i className={`fa fa-pencil small me-1`} />
        {t('edit')}
      </Button>
      <Modal show={isOpen} centered>
        <Modal.Header className="h4 mb-0">{t('workrooms.documents.edit-file-or-folder-modal-header')}</Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group>
              <Form.Label>{t('workrooms.documents.edit-file-or-folder-name-label')}</Form.Label>
              <Form.Control
                {...register('name', {
                  required: { message: t('workrooms.documents.edit-file-or-folder-name-validation-text'), value: true },
                })}
                type="text"
                placeholder={t('workrooms.documents.edit-file-or-folder-name-placeholder')}
                disabled={isLoading}
                className={`${errors?.name ? 'is-invalid' : ''}`}
              />
              {errors?.name && <p className="invalid-feedback">{errors?.name.message}</p>}
            </Form.Group>
            <ButtonToolbar className="justify-content-between mt-3">
              <Button type="submit" variant="secondary" disabled={isLoading}>
                {t('save')}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={() => handleClose()} disabled={isLoading}>
                {t('abort')}
              </Button>
            </ButtonToolbar>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  )
}
export default WorkroomEditFileButton
